import type { RelationPermissionsReturn } from '#layers/forms/types/permissions';
import type { UseRelationPermissionsOptions } from './use-relation-permissions-base';
import { computed } from 'vue';
import { useRelationPermissionsBase } from './use-relation-permissions-base';

export function useO2MPermissions(options: UseRelationPermissionsOptions): RelationPermissionsReturn {
	const {
		permissionsStore,
		oppositeColId,
		oppositeConstrainedFieldIds,
		isCreateForm,
		relationAllows,
		useRelatedWriteContexts,
	} = useRelationPermissionsBase(options);

	// O2M: Constrained FK on OPPOSITE collection. The staged operations ride
	// the parent form's relation field value, so the API terms come from the
	// parent payload's nested operations.
	const canConnect = computed(() => {
		if (!oppositeColId.value) return false;
		if (!relationAllows((relation) => relation.connect())) return false;
		return permissionsStore.canRead(oppositeColId.value)
			&& permissionsStore.canUpdate(oppositeColId.value)
			&& permissionsStore.canUpdateFields(oppositeColId.value, oppositeConstrainedFieldIds.value);
	});

	const canCreate = computed(() => {
		if (!oppositeColId.value) return false;
		if (!relationAllows((relation) => relation.create() !== undefined)) return false;
		return permissionsStore.canCreate(oppositeColId.value)
			&& permissionsStore.canCreateFields(oppositeColId.value, oppositeConstrainedFieldIds.value);
	});

	const canDisconnect = computed(() => {
		if (!oppositeColId.value) return false;
		// On a create form, disconnecting only unstages a pending connect —
		// no wire operation is involved.
		if (!isCreateForm.value && !relationAllows((relation) => relation.disconnect())) return false;
		return permissionsStore.canUpdate(oppositeColId.value)
			&& permissionsStore.canUpdateFields(oppositeColId.value, oppositeConstrainedFieldIds.value);
	});

	return { canConnect, canCreate, canDisconnect, ...useRelatedWriteContexts(canCreate) };
}
